import { Controller } from "@hotwired/stimulus"

// Generic expand/collapse for a trigger + body pair within a single element.
// Lets a section or card header toggle the visibility of its own content.
//
// Usage:
//   <div data-controller="collapsible" data-collapsible-collapsed-value="true">
//     <button data-collapsible-target="trigger"
//             data-action="collapsible#toggle"
//             aria-expanded="true">Header</button>
//     <div data-collapsible-target="body">…</div>
//   </div>
//
// Nested collapsible controllers each manage their own trigger/body targets,
// so a collapsible section can contain individually collapsible cards.
//
// Opt-in persistence: add `data-collapsible-persist-key-value="<id>"` and the
// open/closed state is remembered per-browser in localStorage, restored on the
// next visit (overriding the server-rendered default). Without a persist key
// the controller is stateless, so other collapsibles are unaffected.
export default class extends Controller {
  static targets = ["body", "trigger"]
  static values = { collapsed: Boolean, persistKey: String }

  connect() {
    if (!this.hasPersistKeyValue) return

    const stored = this.readStored()
    if (stored !== null) this.collapsedValue = stored
  }

  toggle() {
    this.collapsedValue = !this.collapsedValue
    this.persist()
  }

  // The Stimulus value IS the state. Stimulus calls this on connect with the
  // initial value and again on every change, so the DOM always reflects
  // `collapsedValue` — no separate class-read needed.
  collapsedValueChanged(collapsed) {
    this.element.classList.toggle("is-collapsed", collapsed)
    this.bodyTargets.forEach((body) => {
      body.hidden = collapsed
    })
    this.triggerTargets.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", String(!collapsed))
    })
  }

  // Persist only on explicit user toggle so a restore-on-connect never
  // clobbers a previously-saved preference.
  persist() {
    if (!this.hasPersistKeyValue) return

    try {
      window.localStorage.setItem(this.storageKey, this.collapsedValue ? "1" : "0")
    } catch {
      // Private mode / disabled storage — persistence is best-effort.
    }
  }

  readStored() {
    try {
      const value = window.localStorage.getItem(this.storageKey)
      return value === null ? null : value === "1"
    } catch {
      return null
    }
  }

  get storageKey() {
    return `collapsible:${this.persistKeyValue}`
  }
}
